enum Direction {
  NONE = 1,
  LEFT = 2,
  RIGHT = 4,
  UP = 8,
  DOWN = 16,
  HORIZONTAL = LEFT | RIGHT,
  VERTICAL = UP | DOWN,
  ALL = HORIZONTAL | VERTICAL,
}

// gestures might have an input state like the PanGesture
enum GestureState {
  BEGIN = 1,
  PROGRESS = 2,
  END = 4,
  CANCEL = 8,
}

type Gesture = {
  id: number /* a unique id of this gesture, which is calculated based on the raw pointer events (hash)
                this is useful to track a stateful gesture like a Pan or Pinch Gesture */;
  name: string; // the name of this gesture, this could be e.g. "pan", "tap", "doubletap" or something like "doubletapthenpan"
  // type: string; // this might be necessary for javascript to distinguish between the gestures, but currently isn't necessary
  pointerType: 'touch' | 'pen' | 'mouse'; // disallow gestures consisting out of multiple pointerTypes, to avoid unnecessary complexity
  pointerCount: number; // currently only 'pointerdown' is counted (so 'pointerover' events are not registered (yet?))
  // pointers: Pointer[]; // not used yet, as there isn't a need currently for that, as hammerjs is broken with the new unified PointerEvent
  timestamp: number; // timestamp of when the gesture started
};

type State = {
  state: GestureState;
};

type Center = {
  relativeCenter: { x: number; y: number }; // center of all pointers (average) relative to the element
  viewportCenter: { x: number; y: number }; // center of all pointers (average) relative to the viewport
};

// type Pointer = Center; // currently just contains the position of the pointer

type PanGesture = Gesture &
  State &
  Center & {
    panDelta: { x: number; y: number; total: number }; // delta since the beginning of the gesture (state GestureState.BEGIN)
    /*
     * Velocity might be calculated using an estimation algorithm like these presented here:
     * https://android.googlesource.com/platform/frameworks/native/+/refs/heads/master/libs/input/VelocityTracker.cpp
     * the velocity is given in px/s
     */
    panVelocity: { x: number; y: number; total: number };
    panDirection: Direction; // Direction moved since starting of the gesture
  };

type PinchGesture = Gesture &
  State &
  Center & {
    pinchScale: number; // delta scale since the beginning of the gesture with state GestureState.BEGIN
    /*
     * the distance between the pointers
     * if there are more than 2 pointers, the distance is the average of all possible distances
     * e.g. with 3 pointers (a, b, c): avg(dist(a, b), dist(a, c), dist(b, c))
     */
    pinchDistance: number;
    pinchVelocity: number; // similar as PanGesture.panVelocity
  };

type RotateGesture = Gesture &
  State &
  Center & {
    rotateDeltaAngle: number; // delta angle (counterclockwise) since the beginning of the gesture (state GestureState.BEGIN)
    rotateAbsoluteAngle: number; // angle relative to the 'north' direction counterclockwise in degrees ([0..360])
    rotateAngleVelocity: number; // degrees/s
  };

type PinchPanRotateGesture = PanGesture & PinchGesture & RotateGesture;

type TapGesture = Gesture &
  Center & {
    tapCount: number;
  };

// as described in Gesture.type, it might be necessary for the non-typed javascript to distinguish between gestures with
// an extra type attribute
type PressGesture = Gesture & State & Center;

export {
  Center,
  Direction,
  GestureState,
  Gesture,
  PanGesture,
  PressGesture,
  PinchPanRotateGesture,
  TapGesture,
  // for simplicity currently don't support sole pinch gestures (yet)
  // PinchGesture,
};
